import {
    Button,
    Dropdown,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Option,
    OptionGroup,
    OptionOnSelectData,
    SelectionEvents,
    Text,
    Tooltip,
} from '@fluentui/react-components';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import React, { useEffect, useState } from 'react';
import { Column, Row } from '../../common';

type DropdownMenuProps = {
    title?: string;
    onOptionChanged?: (option: string) => void;
    /** Options with `selectable: false` raise `onOptionChanged` but never become the selected value. */
    options: {
        label: string;
        value: string | number;
        component?: JSX.Element;
        group?: string;
        icon?: JSX.Element;
        selectable?: boolean;
    }[];
    dataTestId?: string;
    size?: 'small' | 'medium' | 'large';
    className?: string;
    placeholder?: string;
    group?: boolean;
    tabIndex?: number;
    initialValue?: string | number;
    width?: string;
    appearance?: 'underline' | 'outline' | 'filled-darker' | 'filled-lighter';
    compact?: boolean;
    compactIcon?: JSX.Element;
    tooltip?: string;
    valueClass?: string;
    textSize?: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000;
    textWeight?: 'medium' | 'regular' | 'semibold' | 'bold';
};
export const DropdownMenu = (props: DropdownMenuProps) => {
    const {
        onOptionChanged,
        title,
        options,
        dataTestId,
        size = 'small',
        className,
        placeholder,
        group = false,
        tabIndex,
        initialValue,
        width,
        appearance = 'outline',
        compact = false,
        compactIcon,
        tooltip,
        valueClass,
        textSize = 300,
        textWeight,
    } = props;

    const getTextSize = () => {
        if(size === 'large') return textSize + 100;
        if(size === 'small') return textSize - 100;
        return textSize;
    };

    //State
    const [dropdownLabel, setDropdownLabel] = useState(
        initialValue !== undefined ? (options.find((option) => option.value === initialValue)?.label ?? '') : '',
    );
    const [dropdownValue, setDropdownValue] = useState(initialValue ?? '');
    const [dropdownIcon, setDropdownIcon] = useState(
        initialValue !== undefined ? (options.find((option) => option.value === initialValue)?.icon ?? null) : null,
    );

    //Styles
    const commonStyles = useCommonStyles();

    //Logic
    const onOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
        event.stopPropagation();
        const selectedValue = data.optionValue ?? '';
        const selectedOption = options.find((option) => option.value === selectedValue);
        if (selectedOption?.selectable !== false) {
            setDropdownLabel(selectedOption?.label ?? '');
            setDropdownValue(selectedValue);
            setDropdownIcon(selectedOption?.icon ?? null);
        }

        !!onOptionChanged && onOptionChanged?.(selectedValue);
    };

    const setValue = () => {
        setDropdownLabel(
            !!initialValue ? `${options.find((option) => option.value === initialValue)?.label ?? initialValue ?? ''}` : '',
        );
        setDropdownValue(
            !!initialValue ? `${options.find((option) => option.value === initialValue)?.value ?? initialValue ?? ''}` : '',
        );
        setDropdownIcon(!!initialValue ? (options.find((option) => option.value === initialValue)?.icon ?? null) : null);
    };

    //Effects
    useEffect(() => {
        setValue();
    }, [options.length, initialValue]);

    const onMenuItemSelect = (optionValue: string | number) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        const selectedValue = `${optionValue}`;
        const selectedOption = options.find((option) => `${option.value}` === selectedValue);
        if (selectedOption?.selectable !== false) {
            setDropdownLabel(selectedOption?.label ?? '');
            setDropdownValue(selectedValue);
            setDropdownIcon(selectedOption?.icon ?? null);
        }
        !!onOptionChanged && onOptionChanged?.(selectedValue);
    };

    const compactMenu = (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    size={size}
                    appearance='secondary'
                    className={className}
                    icon={compactIcon || dropdownIcon}
                    tabIndex={tabIndex}
                >
                    {!compactIcon && <>{dropdownLabel || title || placeholder || 'Select'}</>}
                </Button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {options.map((option) => (
                        <MenuItem
                            key={option.value}
                            onClick={onMenuItemSelect(option.value)}
                            data-testid={`${dataTestId}-${option.value}-option`}
                        >
                            {option.icon && <span style={{ marginRight: '8px' }}>{option.icon}</span>}
                            {option.component || option.label}
                        </MenuItem>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
    if (compact) {
        return (
            <>
                {!!tooltip ? (
                    <Tooltip content={tooltip} relationship='description'>
                        <span style={{ display: 'inline-flex' }}>{compactMenu}</span>
                    </Tooltip>
                ) : (
                    <>{compactMenu}</>
                )}
            </>
        );
    }

    return (
        <>
            <Dropdown
                className={className}
                style={{ minWidth: width ? 'unset' : undefined, width }}
                tabIndex={tabIndex}
                appearance={appearance}
                size={size}
                button={
                    <Row gap='medium'>
                        {dropdownIcon && (
                            <Column flex='0 0 auto'>
                                {dropdownIcon}
                            </Column>
                        )}
                        <Column>
                            <Text size={getTextSize()}>
                                {title && (
                                    <Text weight='semibold' size={getTextSize()}>
                                        {title} -
                                    </Text>
                                )}{' '}
                                {!!dropdownLabel && (
                                    <Text size={getTextSize()} weight={textWeight} className={valueClass}>
                                        {dropdownLabel}
                                    </Text>
                                )}
                                {!dropdownLabel && <Text className={commonStyles.text.subtleText}>{placeholder}</Text>}{' '}
                            </Text>
                        </Column>
                    </Row>
                }
                listbox={{
                    className: mergeClasses(
                        commonStyles.dropdown.classes.dropdownList,
                        commonStyles.scroll.classes.scrollbarClasses,
                    ),
                    style: { minWidth: 'max-content' },
                }}
                positioning={{ autoSize: false }}
                selectedOptions={[dropdownValue as string]}
                onOptionSelect={onOptionSelect}
                data-testid={dataTestId}
            >
                {group
                    ? Object.entries(
                          options.reduce(
                              (acc, option) => {
                                  const key = option.group ?? '';
                                  if (!acc[key]) acc[key] = [];
                                  acc[key].push(option);
                                  return acc;
                              },
                              {} as Record<string, typeof options>,
                          ),
                      ).map(([groupName, groupOptions]) => (
                          <React.Fragment key={groupName}>
                              {groupName && (
                                  <OptionGroup label={groupName}>
                                      {groupOptions.map((option) => (
                                          <Option
                                              key={option.value}
                                              text={option.label}
                                              value={option.value as string}
                                              style={{ whiteSpace: 'nowrap' }}
                                              data-testid={`${dataTestId}-${option.value}-option`}
                                          >
                                              {option.icon && <span style={{ marginRight: '.5rem' }}>{option.icon}</span>}
                                              {option.component || option.label}
                                          </Option>
                                      ))}
                                  </OptionGroup>
                              )}
                          </React.Fragment>
                      ))
                    : options.map((option) => (
                          <Option
                              key={option.value}
                              text={option.label}
                              value={option.value as string}
                              style={{ whiteSpace: 'nowrap' }}
                              data-testid={`${dataTestId}-${option.value}-option`}
                          >
                              {option.icon && <span style={{ marginRight: '8px' }}>{option.icon}</span>}
                              {option.component || option.label}
                          </Option>
                      ))}
            </Dropdown>
        </>
    );
};
